<template>
  <div class="asc-plot-chart-wrapper" data-automation-id="wrapper_plot_chart">
    <asc-spinner-wrapper :loading="isChartLoading">
      <template v-if="getChartType == 'time' && liveChart">
        <template v-if="!noData && !isInvalid">
          <template v-if="isMultipleCharts">
            <asc-plot-time-chart
              v-for="chart in getGroupedTimeCharts"
              :key="chart.chartIndex + 'multiple'"
              :page-plot="pagePlot"
              :mini-plot="miniPlot"
              :mx-rec-plot="mxRecPlot"
              :reports-plot="reportsPlot"
              :chart-index="chart.chartIndex"
              :chart-data="chart.data"
              :multiple-charts="isMultipleCharts"
              :on-update-tab-view-page-plot="onUpdateTabViewPagePlot"
              :on-apply-page-plot="handleApplyPagePlot"
              :hide-hidden-tabs="hideHiddenTabs"
            ></asc-plot-time-chart>
          </template>
          <template v-if="!isMultipleCharts">
            <asc-plot-time-chart
              :page-plot="pagePlot"
              :mini-plot="miniPlot"
              :mx-rec-plot="mxRecPlot"
              :reports-plot="reportsPlot"
              :chart-index="0"
              :chart-data="chartData"
              :multiple-charts="false"
              :on-update-tab-view-page-plot="onUpdateTabViewPagePlot"
              :on-apply-page-plot="handleApplyPagePlot"
              :plot-editor="plotEditor"
              :hide-hidden-tabs="hideHiddenTabs"
            ></asc-plot-time-chart>
          </template>
        </template>
      </template>
      <template v-if="getChartType == 'box' && liveChart">
        <template v-if="!noData && !isInvalid">
          <div
            :class="[
              miniPlot
                ? 'asc-plot-chart-wrapper--mini'
                : 'asc-plot-chart-wrapper--full',
            ]"
          >
            <asc-plot-box-chart
              :page-plot="pagePlot"
              :mini-plot="miniPlot"
              :mx-rec-plot="mxRecPlot"
              :reports-plot="reportsPlot"
              :chart-index="0"
              :chart-data="chartData"
              :disabled="disabled"
            ></asc-plot-box-chart>
          </div>
        </template>
      </template>
      <asc-no-data v-if="noData && !isInvalid"></asc-no-data>
      <asc-centered-text
        v-if="isInvalid"
        class="asc-centered-text"
        :value="getInvalidText"
      ></asc-centered-text>
    </asc-spinner-wrapper>
  </div>
</template>

<script>
import AscSpinnerWrapper from '@/components/atoms/AscSpinnerWrapper'
import AscNoData from '@/components/atoms/AscNoData'
import AscCenteredText from '@/components/atoms/AscCenteredText'
import AscPlotTimeChart from '@/components/molecules/AscPlotTimeChart'
import AscPlotBoxChart from '@/components/molecules/AscPlotBoxChart'
import { getChartTypeGivenPlotTypeId } from '@/utilities/plotting'
import _ from 'lodash'

export default {
  name: 'AscPlotChartWrapper',

  components: {
    AscSpinnerWrapper,
    AscNoData,
    AscCenteredText,
    AscPlotTimeChart,
    AscPlotBoxChart,
  },

  props: {
    pagePlot: {
      type: Object,
      default: () => {},
    },
    loadingPlotIds: {
      type: Array,
      default: () => [],
    },
    chartData: {
      type: Array,
      default: () => [],
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    noData: {
      type: Boolean,
      default: false,
    },
    miniPlot: {
      type: Boolean,
      default: false,
    },
    mxRecPlot: {
      type: Boolean,
      default: false,
    },
    reportsPlot: {
      type: Boolean,
      default: false,
    },
    liveChart: {
      type: Boolean,
      default: true,
    },
    onUpdateTabViewPagePlot: {
      type: Function,
      default: () => {},
    },
    onApplyPagePlot: {
      type: Function,
      default: () => {},
    },
    plotEditor: {
      type: Boolean,
      default: false,
    },
    hideHiddenTabs: {
      type: Boolean,
      default: false,
    },
  },

  computed: {
    getInvalidText() {
      return 'The plot cannot be rendered due to a configuration error or access error.'
    },

    isInvalid() {
      return this.pagePlot.isInvalid
    },

    isChartLoading() {
      return this.loadingPlotIds.includes(this.pagePlot.id)
    },

    isMultipleCharts() {
      const items = this.chartData.filter(
        item =>
          item.plotId === this.pagePlot.id &&
          item.chartIndex > 0 &&
          !item.tabHidden,
      )

      return items.length > 0
    },

    getChartType() {
      return getChartTypeGivenPlotTypeId(this.pagePlot.plotTypeId)
    },

    getGroupedTimeCharts() {
      const items = this.chartData.filter(
        item => item.plotId === this.pagePlot.id && !item.tabHidden,
      )
      const grouped = _.groupBy(items, 'chartIndex')
      const mapped = Object.keys(grouped).map((item, index) => {
        return {
          chartIndex: Number(item),
          data: Object.values(grouped)[index],
        }
      })

      return mapped
    },
    getSingleTimeChart() {
      const items = this.chartData.filter(
        item => item.plotId === this.pagePlot.id && !item.tabHidden,
      )
      const grouped = _.groupBy(items, 'chartIndex')
      const mapped = Object.keys(grouped).map((item, index) => {
        return {
          chartIndex: Number(item),
          data: Object.values(grouped)[index],
        }
      })

      return mapped
    },
  },

  watch: {
    pagePlot: {
      deep: true,
      handler(newPagePlot) {
        this.pagePlot = newPagePlot
      },
    },
    loadingPlotIds(newVal, oldVal) {
      if (newVal !== oldVal) {
        this.loadingPlotIds = newVal
      }
    },
    disabled(newVal, oldVal) {
      if (newVal !== oldVal) {
        this.disabled = newVal
      }
    },
    noData(newVal, oldVal) {
      if (newVal !== oldVal) {
        this.noData = newVal
      }
    },
    miniPlot(newVal, oldVal) {
      if (newVal !== oldVal) {
        this.miniPlot = newVal
      }
    },
  },

  methods: {
    handleApplyPagePlot(plotId, plotTypeId, event, options) {
      if (this.onApplyPagePlot) {
        this.onApplyPagePlot(plotId, plotTypeId, event, options)
      }
    },
  },
}
</script>

<style lang="scss">
.asc-plot-chart-wrapper {
  &--image {
    width: 100%;
  }

  &--legend {
    padding: 0 16px 16px 16px;
  }

  .asc-plot-chart-wrapper--mini {
    max-height: 280px;
    overflow-y: hidden;
    margin-bottom: 20px;
    width: 100%;

    .asc-plot-box-chart {
      max-height: 280px;
      overflow-y: auto;
    }
  }

  .asc-centered-text {
    height: 300px;
  }
}
</style>
